import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import styles from './MultiSelect.module.css';

interface SelectOption {
    id: string;
    name: string;
    environment?: string;
}

interface MultiSelectProps {
    label?: string;
    options: SelectOption[];
    selected: SelectOption[];
    onChange: (selected: SelectOption[]) => void;
    maxItems?: number;
    placeholder?: string;
    disabled?: boolean;
}

const envClass: Record<string, string> = {
    prod: styles.envProd,
    test: styles.envTest,
    dev: styles.envDev,
};

function EnvBadge({ env }: { env?: string }) {
    if (!env) return null;
    return (
        <span className={`${styles.envBadge} ${envClass[env] ?? styles.envNone}`}>
            {env}
        </span>
    );
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    label,
    options,
    selected,
    onChange,
    maxItems = 100,
    placeholder = 'Select items...',
    disabled = false,
}) => {
    const [input, setInput] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();
    const [listboxStyle, setListboxStyle] = useState<CSSProperties | null>(null);

    const updateListboxPosition = useCallback(() => {
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const viewportPadding = 8;
        const dropdownGap = 6;
        const maxMenuHeight = 220;
        const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
        const spaceAbove = rect.top - viewportPadding;
        const openUpwards = spaceBelow < 160 && spaceAbove > spaceBelow;
        const availableHeight = Math.max(
            120,
            Math.min(maxMenuHeight, (openUpwards ? spaceAbove : spaceBelow) - dropdownGap)
        );

        setListboxStyle(openUpwards
            ? {
                position: 'fixed',
                left: rect.left,
                bottom: window.innerHeight - rect.top + dropdownGap,
                width: rect.width,
                maxHeight: availableHeight,
                zIndex: 1000,
            }
            : {
                position: 'fixed',
                top: rect.bottom + dropdownGap,
                left: rect.left,
                width: rect.width,
                maxHeight: availableHeight,
                zIndex: 1000,
            });
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const clickedInsideWrapper = !!wrapperRef.current?.contains(target);
            const clickedInsideListbox = !!listboxRef.current?.contains(target);

            if (!clickedInsideWrapper && !clickedInsideListbox) {
                setOpen(false);
                setActiveIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleItem = (item: SelectOption) => {
        const exists = selected.some((sel) => sel.id === item.id);
        if (exists) {
            onChange(selected.filter((sel) => sel.id !== item.id));
        } else {
            if (selected.length >= maxItems) return;
            onChange([...selected, item]);
        }
        setInput('');
        setOpen(true);
        setActiveIndex(-1);
    };

    const filteredOptions = options.filter(
        (opt) =>
            !selected.some((sel) => sel.id === opt.id) &&
            opt.name.toLowerCase().includes(input.toLowerCase())
    );

    useEffect(() => {
        if (!open || filteredOptions.length === 0) {
            setActiveIndex(-1);
            return;
        }

        if (activeIndex >= filteredOptions.length) {
            setActiveIndex(filteredOptions.length - 1);
        }
    }, [activeIndex, filteredOptions.length, open]);

    useLayoutEffect(() => {
        if (!open) return;

        updateListboxPosition();

        window.addEventListener('resize', updateListboxPosition);
        window.addEventListener('scroll', updateListboxPosition, true);

        return () => {
            window.removeEventListener('resize', updateListboxPosition);
            window.removeEventListener('scroll', updateListboxPosition, true);
        };
    }, [open, updateListboxPosition]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && input.length === 0 && selected.length > 0) {
            event.preventDefault();
            toggleItem(selected[selected.length - 1]);
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) {
                setOpen(true);
            }
            if (filteredOptions.length > 0) {
                setActiveIndex((current) => Math.min(current + 1, filteredOptions.length - 1));
            }
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (filteredOptions.length > 0) {
                setActiveIndex((current) => Math.max(current - 1, 0));
            }
            return;
        }

        if (event.key === 'Enter' && open && activeIndex >= 0 && filteredOptions[activeIndex]) {
            event.preventDefault();
            toggleItem(filteredOptions[activeIndex]);
            return;
        }

        if (event.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
        }
    };

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            {label && <label className={styles.label}>{label}</label>}
            <div
                className={`${styles.control} ${disabled ? styles.controlDisabled : ''}`}
                onClick={() => {
                    if (disabled) return;
                    setOpen(true);
                    inputRef.current?.focus();
                }}
            >
                <div className={styles.valueArea}>
                    {selected.map((item) => (
                        <span key={item.id} className={styles.selectedItem}>
                            {item.name}
                            <EnvBadge env={item.environment} />
                            <button
                                type="button"
                                className={styles.removeBtn}
                                aria-label={`Remove ${item.name}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleItem(item);
                                }}
                                disabled={disabled}
                            >
                                &times;
                            </button>
                        </span>
                    ))}
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(event) => {
                            setInput(event.target.value);
                            setOpen(true);
                            setActiveIndex(-1);
                        }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={handleKeyDown}
                        placeholder={selected.length === 0 ? placeholder : ''}
                        className={styles.inlineInput}
                        disabled={disabled || selected.length >= maxItems}
                        aria-expanded={open}
                        aria-controls={open ? listboxId : undefined}
                        aria-autocomplete="list"
                    />
                </div>
            </div>
            {open && listboxStyle && createPortal(
                <div className={styles.suggestionsList} role="listbox" id={listboxId} style={listboxStyle} ref={listboxRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <button
                                type="button"
                                key={opt.id}
                                className={`${styles.suggestionItem} ${index === activeIndex ? styles.suggestionItemActive : ''}`}
                                role="option"
                                aria-selected={index === activeIndex}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => toggleItem(opt)}
                            >
                                <span className={styles.optionName}>{opt.name}</span>
                                <EnvBadge env={opt.environment} />
                            </button>
                        ))
                    ) : (
                        <div className={styles.emptySuggestions}>
                            {input ? 'No matching items' : 'No more items available'}
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default MultiSelect;
